import api from './api';
import { toQueryDateYMD } from '../utils/formatters';

export { esOrdenCompraAnulada } from '../utils/ordenCompraFechaProbable';

export interface OrdenCompraDetalle {
  nroIdformulario: number;
  nroItem: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad?: number;
  cantidadCancelada?: number;
  precioLista?: number;
  porcDesc?: number;
  precioUnitario?: number;
  descuento?: number;
  subtotal?: number;
  tasa1?: number;
  tasa2?: number;
  tasa3?: number;
  tasa4?: number;
  tasa5?: number;
  impInterno1?: number;
  impInterno2?: number;
  tasaHidrica?: number;
  notas?: string;
  tipoProveedor?: string;
  nroCuenta?: number;
}

export interface OrdenCompra {
  nroFormulario: number;
  fechaEmision?: string | Date;
  fechaVencim?: string | Date;
  horaEmision?: string | Date;
  nroTipoFormulario?: string;
  tipoCliente?: string;
  letra?: string;
  tipoIva?: string;
  nroSuc: number;
  nroComprobante?: number;
  nroRemito?: string;
  nroProveedor?: number;
  nombre?: string;
  cuit?: string;
  nroLista?: string;
  codigoMoneda?: number;
  subtotal?: number;
  descuento?: number;
  importeSinIva?: number;
  exento?: number;
  ivainscripto?: number;
  ivanoInscripto?: number;
  ivaotro?: number;
  importeTotal?: number;
  asentado?: string;
  impreso?: string;
  /** Aprobación de la OC (habilita la impresión). Requiere que el backend lo devuelva. */
  aprobada?: boolean;
  fechaAprobacion?: string | null;
  usuarioAprobacion?: string | null;
  descripcion?: string;
  usuario?: string;
  nroUbicacionDestino?: number;
  ubicacionDestinoDescripcion?: string;
  formaDePago?: string;
  formaEntrega?: string;
  ordenDeCompra?: string;
  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;
  detalles?: OrdenCompraDetalle[];
}

export interface OrdenCompraSearchParams {
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
  fechaDesde?: Date | string;
  fechaHasta?: Date | string;
  nroProveedor?: string;
  asentado?: string;
  usuario?: string;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
}

export interface OrdenCompraPagedResult {
  items: OrdenCompra[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface OrdenCompraPendienteLinea {
  nroItem: number;
  codigoArticulo?: string | null;
  descripcionArticulo?: string | null;
  um?: string | null;
  cantidadPedida: number;
  cantidadRecibida: number;
  cantidadCancelada: number;
  cantidadPendiente: number;
  cantidadCanceladaMaxima: number;
}

export interface OrdenCompraPendientes {
  nroFormulario: number;
  anulada: boolean;
  aprobada: boolean;
  puedeCancelarCantidades: boolean;
  totalPendiente: number;
  lineas?: OrdenCompraPendienteLinea[] | null;
}

export interface CancelarCantidadItem {
  nroItem: number;
  cantidadCancelada: number;
}

export interface CancelarCantidadesOrdenCompraPayload {
  cancelarTodo: boolean;
  items?: CancelarCantidadItem[] | null;
  motivo?: string | null;
}

/** Misma forma que el alta de remito de compra (API reutiliza RemitoCompra* DTOs). */
export interface OrdenCompraCreatePayload {
  formulario: Record<string, unknown>;
  items: Record<string, unknown>[];
}

const ordenCompraService = {
  getOrdenesCompra: async (params: OrdenCompraSearchParams): Promise<OrdenCompraPagedResult> => {
    const queryParams: Record<string, unknown> = {
      PageNumber: params.pageNumber,
      PageSize: params.pageSize,
      SearchTerm: params.searchTerm || undefined,
      NroProveedor: params.nroProveedor || undefined,
      Asentado: params.asentado || undefined,
      Usuario: params.usuario || undefined,
      Letra: params.letra || undefined,
      NroSuc: params.nroSuc ?? undefined,
      NroComprobante: params.nroComprobante ?? undefined,
    };

    if (params.fechaDesde) {
      queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
    }
    if (params.fechaHasta) {
      queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
    }

    const response = await api.get<OrdenCompraPagedResult>('/api/OrdenesCompra', { params: queryParams });
    return response.data;
  },

  getOrdenCompra: async (id: number): Promise<OrdenCompra> => {
    const response = await api.get<OrdenCompra>(`/api/OrdenesCompra/${id}`);
    return response.data;
  },

  createOrdenCompra: async (payload: OrdenCompraCreatePayload): Promise<OrdenCompra> => {
    const response = await api.post<OrdenCompra>('/api/OrdenesCompra', payload);
    return response.data;
  },

  getOrdenCompraPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/api/OrdenesCompra/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  puedeAnularOrdenCompra: async (id: number): Promise<{ puedeAnular: boolean; motivos: string[] }> => {
    const response = await api.get<{ puedeAnular: boolean; motivos: string[] }>(
      `/api/OrdenesCompra/${id}/puede-anular`
    );
    return response.data;
  },

  anularOrdenCompra: async (id: number, dto?: { motivo?: string }): Promise<void> => {
    await api.post(`/api/OrdenesCompra/${id}/anular`, dto ?? {});
  },

  /**
   * Actualiza la fecha probable de entrega (`fechaVencim`) de una OC emitida/confirmada.
   * Requiere endpoint backend: PATCH /api/OrdenesCompra/{id}/fecha-vencim
   */
  actualizarFechaVencim: async (id: number, fechaVencim: string): Promise<OrdenCompra | void> => {
    const response = await api.patch<OrdenCompra>(`/api/OrdenesCompra/${id}/fecha-vencim`, {
      fechaVencim,
    });
    return response.data;
  },

  /**
   * Aprueba la OC (registra fecha y usuario). Una OC sin aprobar no se puede imprimir.
   * Requiere endpoint backend: POST /api/OrdenesCompra/{id}/aprobar
   */
  aprobarOrdenCompra: async (id: number): Promise<OrdenCompra | void> => {
    const response = await api.post<OrdenCompra>(`/api/OrdenesCompra/${id}/aprobar`, {});
    return response.data;
  },

  /** Cantidades pedidas / recibidas / canceladas / pendientes por ítem de la OC. */
  getPendientesOrdenCompra: async (id: number): Promise<OrdenCompraPendientes> => {
    const response = await api.get<OrdenCompraPendientes>(`/api/OrdenesCompra/${id}/pendientes`);
    return response.data;
  },

  /**
   * Cancela total o parcialmente las cantidades pendientes de recibir (no anula la OC).
   * Con `cancelarTodo` se cancela todo el pendiente de todos los ítems.
   */
  cancelarCantidadesOrdenCompra: async (
    id: number,
    payload: CancelarCantidadesOrdenCompraPayload
  ): Promise<OrdenCompraPendientes> => {
    const response = await api.post<OrdenCompraPendientes>(
      `/api/OrdenesCompra/${id}/cancelar-cantidades`,
      payload
    );
    return response.data;
  },
};

export default ordenCompraService;
